/**
 * Message bubble component for AI chat messages.
 * Renders user and assistant messages with markdown support and streaming.
 */

import type { UIMessage } from "@convex-dev/agent/react";
import { useSmoothText } from "@convex-dev/agent/react";
import { cn } from "@plasma/utils/cn";
import Markdown from "react-markdown";

export function MessageBubble({ message }: { message: UIMessage }) {
  const isUser = message.role === "user";
  const isStreaming = message.status === "streaming";

  // Use smooth text for streaming messages
  const [visibleText] = useSmoothText(message.text ?? "", {
    startStreaming: isStreaming,
  });

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-4 py-2 text-sm",
          isUser
            ? "bg-primary text-primary-foreground whitespace-pre-wrap"
            : "bg-muted text-muted-foreground",
          message.status === "pending" && "animate-pulse"
        )}
      >
        {/* Agent name for assistant messages */}
        {!isUser && message.agentName && (
          <div className="mb-1 text-xs opacity-70">{message.agentName}</div>
        )}

        {/* Message content */}
        {isUser ? (
          (() => {
            const userText = isStreaming
              ? visibleText || "..."
              : message.text || "...";
            return userText;
          })()
        ) : (
          <div className="prose prose-sm dark:prose-invert prose-headings:my-2 prose-li:my-0.5 prose-ol:my-1 prose-p:my-1 prose-ul:my-1 max-w-none">
            <Markdown>
              {isStreaming ? visibleText || "..." : message.text || "..."}
            </Markdown>
          </div>
        )}

        {/* Status indicator */}
        {isStreaming && (
          <span className="ml-1 inline-block animate-pulse">|</span>
        )}
      </div>
    </div>
  );
}
